import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
    addPreLiqItemsToLiquidation,
    getPendingPreLiqItemsForLiquidation,
} from '../../servicesHooks/Honorarium/useHonorariumPreLiquidationService';
import { parseApiError } from '../../services/apiError';
import { HonorariumPreLiquidationItemDTO } from '../../dto/HonorariumPreLiquidationDTO';
import { LIQUIDATION_ERROR_CODES } from '../../dto/HonorariumLiquidationDTO';
import { formatMoney } from '../../utils/money';
import { formatDateOnly } from '../../utils/date';
import useNotify from '../../hooks/useNotify';
import FieldError from '../../components/FieldError/FieldError';

interface AddItemsDialogProps {
    visible: boolean;
    liquidationId: number;
    /** Desde la vista de un profesional: solo sus ítems. Sin él, los de todos los profesionales de la entidad. */
    professionalId?: number;
    onHide: () => void;
    onAdded: () => void;
}

/** Agrega a una liquidación abierta ítems de pre-liquidación pendientes de su entidad facturadora. */
export default function AddItemsDialog({ visible, liquidationId, professionalId, onHide, onAdded }: AddItemsDialogProps) {
    const notify = useNotify();

    const [items, setItems] = useState<HonorariumPreLiquidationItemDTO[]>([]);
    const [selected, setSelected] = useState<HonorariumPreLiquidationItemDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectionError, setSelectionError] = useState<string | null>(null);

    // La lista de pendientes de la entidad no está paginada: el filtro por profesional va en el front
    const visibleItems = useMemo(() => (professionalId != null
        ? items.filter(item => item.professional === professionalId)
        : items), [items, professionalId]);

    const load = useCallback(async () => {
        setLoading(true);
        setSelected([]);
        setSelectionError(null);
        try {
            setItems(await getPendingPreLiqItemsForLiquidation(liquidationId) ?? []);
        } catch (error: unknown) {
            parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
            notify.error('No se pudieron cargar los ítems pendientes. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [liquidationId, notify]);

    useEffect(() => {
        if (visible) load();
    }, [visible, load]);

    async function handleAdd() {
        if (selected.length === 0) {
            setSelectionError('Seleccioná al menos un ítem');
            return;
        }
        setSaving(true);
        try {
            await addPreLiqItemsToLiquidation({
                liquidationId,
                preLiqItemIds: selected.map(item => item.honorariumPreLiquidationItem),
            });
            notify.success(selected.length === 1 ? '1 ítem agregado' : `${selected.length} ítems agregados`);
            onAdded();
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            if (apiError.errorCode === LIQUIDATION_ERROR_CODES.PRELIQUIDATION_ITEMS_UNAVAILABLE) {
                // Otra persona liquidó o eliminó algún ítem: se recarga la lista y no se agregó nada
                notify.error(apiError.message);
                load();
                return;
            }
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError ? apiError.message : 'No se pudieron agregar los ítems. Intentá de nuevo.');
        } finally {
            setSaving(false);
        }
    }

    const footer = (
        <div className="md-dialog__footer">
            <Button outlined label="Cancelar" className="md-ghost" onClick={onHide} disabled={saving} />
            <Button
                label={selected.length > 0 ? `Agregar (${selected.length})` : 'Agregar'}
                className="md-primary"
                onClick={handleAdd}
                loading={saving}
            />
        </div>
    );

    return (
        <Dialog
            header="Agregar ítems"
            visible={visible}
            onHide={() => { if (!saving) onHide(); }}
            footer={footer}
            style={{ width: '720px' }}
            breakpoints={{ '760px': '100vw' }}
            className="md-dialog"
            modal
        >
            {loading ? (
                <div className="md-loading" role="status">
                    <ProgressSpinner strokeWidth="4" />
                    <span>Cargando…</span>
                </div>
            ) : (
                <>
                    <DataTable
                        value={visibleItems}
                        dataKey="honorariumPreLiquidationItem"
                        selectionMode="checkbox"
                        selection={selected}
                        onSelectionChange={(e) => {
                            setSelected(e.value as HonorariumPreLiquidationItemDTO[]);
                            setSelectionError(null);
                        }}
                        className="crudTable md-selectable"
                        scrollable
                        scrollHeight="360px"
                        emptyMessage="No hay ítems pendientes para agregar."
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '48px' }} />
                        {professionalId == null && <Column field="professionalName" header="Profesional" />}
                        <Column field="sourceType" header="Origen" />
                        <Column
                            header="Período"
                            body={(row: HonorariumPreLiquidationItemDTO) => `${formatDateOnly(row.fromDate)} – ${formatDateOnly(row.toDate)}`}
                        />
                        <Column field="detailCount" header="Registros" className="text-center" headerClassName="col-center" />
                        <Column
                            header="Honorario"
                            body={(row: HonorariumPreLiquidationItemDTO) => formatMoney(row.professionalAmount)}
                            className="text-right"
                            headerClassName="col-right"
                        />
                    </DataTable>
                    <FieldError id="add-items-selection-error" message={selectionError} />
                </>
            )}
        </Dialog>
    );
}
